import type { ExtractAtomValue, Getter, Setter } from 'jotai';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import type { AnnotationT } from '../../../../labelling/types';
import { createKeyFrame } from '../../../../labelling/utils';
import type { MetadataPayload } from '../../../studio-store/types';
import { buildAnnotationForOutput } from '../../../utils/build-annotation-for-output';
import { isFrameInAnySpan, isKeyframe } from '../../../utils/frame-span-utils';
import { annotationLabelsAtom, annotationMetadataAtom } from '../../annotation-atoms';
import { studioStoreAtom } from '../../studio-store';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import { selectedAnnotationIdsAtom } from '../../tool-atoms';
import type { DeleteAnnotationEventPayload } from '../events';
import { reviseRelationships } from './revise-relationships-mutation';
export const deleteAnnotationCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<DeleteAnnotationEventPayload, void> => {
  let deletedAnnotation: AnnotationT | undefined;
  let deletedAnswers: MetadataPayload['answers'];
  let deletedDynamicAnswers: MetadataPayload['dynamicAnswers'];
  let selectedIds: string[] = [];
  let isNextFramePromoted = false;
  let promotedMetadata: MetadataPayload | undefined;
  let promotedAnnotationLabels: ExtractAtomValue<typeof annotationLabelsAtom> | undefined;
  let restoreRelationships: () => void = () => {};
  return {
    execute: async payload => {
      const {
        frameIndex,
        annotationIdx
      } = payload;
      const studioStore = get(studioStoreAtom);
      const metadata = get(annotationMetadataAtom).get(annotationIdx);
      const annotationLabels = get(annotationLabelsAtom);
      if (!studioStore || !metadata) {
        return;
      }
      const framePayload = await studioStore.getAnnotationFrameAtIndex(frameIndex, annotationIdx);
      deletedAnnotation = buildAnnotationForOutput(framePayload, metadata, annotationLabels, frameIndex);
      deletedAnswers = metadata.answers;
      deletedDynamicAnswers = metadata.dynamicAnswers;
      selectedIds = get(selectedAnnotationIdsAtom);
      if (shouldPromoteNextFrame(metadata, frameIndex)) {
        isNextFramePromoted = true;
        promotedMetadata = metadata;
        promotedAnnotationLabels = annotationLabels;
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.PROMOTE_TO_KEYFRAME,
          frameIndex: frameIndex + 1,
          annotationId: annotationIdx
        });
      }
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.DELETE_ANNOTATION,
        frameIndex,
        annotationId: annotationIdx
      });
      restoreRelationships = reviseRelationships(get, set, annotationIdx, {
        start: frameIndex,
        end: frameIndex
      });
      set(selectedAnnotationIdsAtom, selectedIds.filter(id => id !== annotationIdx));
    },
    undo: async payload => {
      if (!deletedAnnotation) {
        return;
      }
      const {
        frameIndex,
        annotationIdx
      } = payload;
      if (isNextFramePromoted && promotedMetadata) {
        const studioStore = get(studioStoreAtom);
        if (!studioStore) {
          return;
        }
        const nextFramePayload = await studioStore.getAnnotationFrameAtIndex(frameIndex + 1, annotationIdx);
        const nextAnnotation = buildAnnotationForOutput(nextFramePayload, promotedMetadata, promotedAnnotationLabels ?? [], frameIndex + 1);
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION,
          frameIndex: frameIndex + 1,
          annotationId: annotationIdx,
          annotation: {
            ...nextAnnotation,
            keyFrame: createKeyFrame(false)
          }
        });
      }
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION,
        frameIndex,
        annotationId: annotationIdx,
        annotation: deletedAnnotation
      });
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
        annotationId: annotationIdx,
        partialPayload: {
          answers: deletedAnswers,
          dynamicAnswers: deletedDynamicAnswers
        }
      });
      restoreRelationships();
      set(selectedAnnotationIdsAtom, selectedIds);
    }
  };
};
const shouldPromoteNextFrame = (metadata: MetadataPayload, frameIndex: number): boolean => {
  const nextFrameInSpan = isFrameInAnySpan(frameIndex + 1, metadata.spans);
  return isKeyframe(frameIndex, metadata.keyframes) && nextFrameInSpan && !isKeyframe(frameIndex + 1, metadata.keyframes);
};